import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, CalendarDays, Lock, Zap, Table, MessageSquare, CopyPlus, Tag } from 'lucide-react';
import {
  apiAffaires,
  apiLogtime,
  apiLoggedTime,
  apiPersons,
  apiPublicHolidays,
  apiSettings,
} from '../api/client.js';
import Modal from '../components/Modal.jsx';
import AffaireFilterableSelect from '../components/AffaireFilterableSelect.jsx';
import { lastAffaireIn, rememberLastAffaire } from '../lib/lastAffaire.js';
import { logtimeViewModeOverride, releaseLogtimeViewModeOverride } from '../lib/deepLinkFilter.js';
import TaskPicker from '../components/TaskPicker.jsx';
import DurationInput, { formatHHMM } from '../components/DurationInput.jsx';
import LogtimeCalendarView from './LogtimeCalendarView.jsx';
import PersonFormModal from '../components/PersonFormModal.jsx';
import Avatar from '../components/Avatar.jsx';

/**
 * Sprint 3 — Vue Logtime (saisie horaire hebdo).
 * Sprint 5 — Intégration jours fériés (lecture seule, cellules à 0).
 * Sprint 6 — Refonte UX Logtime (Pattern Float) :
 *   - Pré-remplissage des inputs depuis les `suggestions[]` du planning.
 *   - Bouton "Log X:XX" global qui valide TOUTES les suggestions d'un coup
 *     via POST /api/logged-time/bulk.
 *   - Bouton "Log Y:YY" par ligne pour valider les suggestions de l'affaire.
 *   - DurationInput (HH:MM custom) en remplacement des `<input type="number">`.
 *   - Affichage du verrou comptable : bannière + cellules read-only sur les
 *     dates verrouillées + Log all désactivé.
 *   - Badge "planifié" sous les inputs des LoggedTime créés via bulk (source).
 *
 * Comportement :
 *  - USER : voit/édite ses propres heures via /api/logtime/week (le backend
 *    force le scope IDOR sur la Person liée à son email).
 *  - ADMIN : toggle "Ma saisie | Vue admin" :
 *      * Ma saisie : édite ses propres heures.
 *      * Vue admin : picker Person + saisie sur une Person tierce.
 */

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  textAlign: 'center',
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const choiceBtn = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 4,
  textAlign: 'left',
  padding: '10px 14px',
  background: 'var(--glass)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const secondaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

const pillBtn = {
  borderRadius: 999,
  border: '1px solid var(--glass-border)',
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

const rowLogBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'var(--success)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
};

/** Retourne le lundi (UTC, 00:00) de la semaine d'une date donnée. */
function getMonday(date) {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d;
}

function toIsoDate(date) {
  return new Date(date).toISOString().slice(0, 10);
}

function formatRange(weekStart, weekEnd) {
  const fmt = (d) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}

/**
 * Ticket #49 — `initialDate` / `initialPersonId` : ouverture directe sur la
 * semaine (et la personne) d'un jour cliqué depuis le détail d'une tuile
 * d'accueil. Sans ces props, comportement historique (semaine courante, soi).
 */
export default function LogtimeView({ user, initialDate, initialPersonId }) {
  const isAdmin = user?.role === 'ADMIN';
  // Ticket #45 — consulter la semaine d'une autre personne est ouvert à tous
  // les rôles (lecture seule hors ADMIN, cf. `readOnlyWeek` plus bas).
  const canSeeOthers = true;
  const othersLabel = isAdmin ? 'Vue admin' : 'Une autre personne';
  // Édition Person depuis le clic sur le nom (ADMIN).
  const [editPersonId, setEditPersonId] = useState(null);

  // Sprint 12 — bascule Tableau | Calendrier (vue de validation du prévisionnel).
  // Le dernier choix est mémorisé en localStorage et rechargé par défaut.
  // Ticket support #15 — un lien profond filtré sur des personnes impose le
  // Calendrier (le Tableau est mono-personne) sans réécrire la préférence,
  // jusqu'à ce que l'utilisateur choisisse lui-même un mode.
  const forcedViewModeRef = useRef(undefined);
  if (forcedViewModeRef.current === undefined) {
    forcedViewModeRef.current = logtimeViewModeOverride(window.location.search);
  }
  const [viewMode, setViewMode] = useState(() => {
    if (forcedViewModeRef.current) return forcedViewModeRef.current;
    try {
      return window.localStorage.getItem('synolog_logtime_viewmode') === 'calendar'
        ? 'calendar'
        : 'table';
    } catch {
      return 'table';
    }
  });
  useEffect(() => {
    if (forcedViewModeRef.current) {
      if (viewMode === forcedViewModeRef.current) return;
      forcedViewModeRef.current = null;
      releaseLogtimeViewModeOverride();
    }
    try {
      window.localStorage.setItem('synolog_logtime_viewmode', viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  // ---- État semaine + Person ciblée ----
  const [weekStart, setWeekStart] = useState(() =>
    getMonday(initialDate ? new Date(`${initialDate}T00:00:00`) : new Date()),
  );
  const [mode, setMode] = useState(initialPersonId ? 'admin' : 'self');
  // Sprint 9 — Pour MANAGER, `persons` contient sa descendance ; pour ADMIN,
  // toute la liste Person.
  const [persons, setPersons] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState(initialPersonId ?? null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- État de saisie (UI-local) ----
  // key = `${affaireId}|${phaseIdOrEmpty}|${taskIdOrEmpty}|${date}`
  // { hours, notes, billable, existingId?, suggestionId?, allocationId?,
  //   phaseId?, taskId?, task?, affaireId, date, pending? }
  // `affaireId`/`date`/`taskId` sont DUPLIQUÉS dans la valeur : c'est ce qui
  // permet de ne jamais redécouper la clé (ticket #76).
  const [cells, setCells] = useState({});
  const [busyCells, setBusyCells] = useState({}); // saveBusyByCellKey
  const [saveError, setSaveError] = useState(null);

  // Ticket #43 — la cellule reste éditable pendant la sauvegarde (elle n'est
  // plus `readOnly`, ce qui avalait la frappe suivante). Deux garde-fous :
  //  - `cellsRef` : `persistCell` relit l'état AU MOMENT de l'exécution, pas
  //    celui capturé par la closure (sinon la 2e saisie recrée une entrée au
  //    lieu de patcher celle que la 1re vient de créer) ;
  //  - `pendingRef` : les sauvegardes d'une même cellule sont sérialisées, donc
  //    jamais deux POST concurrents sur la même case.
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const pendingRef = useRef({});

  // ---- Affaires ajoutées manuellement ----
  const [extraAffaires, setExtraAffaires] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [availableAffaires, setAvailableAffaires] = useState([]);
  // Sprint 7.2 — id sélectionné dans le combobox (validé avec un bouton).
  const [pickerSelection, setPickerSelection] = useState('');
  // Ticket #76 — tâche optionnelle de la ligne à ajouter. Indispensable pour
  // imputer sur une tâche non planifiée : sans elle, la grille ne saurait
  // proposer que les tâches déjà présentes dans le prévisionnel de la semaine.
  const [pickerTask, setPickerTask] = useState({ taskId: null, taskName: null });

  // ---- Jours fériés (Sprint 5) ----
  const [holidaysByDate, setHolidaysByDate] = useState(new Map());

  // ---- Bulk "Log all" busy state ----
  const [bulkBusy, setBulkBusy] = useState(false);

  // ---- Durée minimale de saisie (paramètre admin, défaut 1h) ----
  const [minHours, setMinHours] = useState(1);
  useEffect(() => {
    apiSettings
      .get()
      .then((s) => setMinHours(Number(s?.logtimeMinHours ?? 1)))
      .catch(() => {});
  }, []);

  // ---- Chargement initial des Persons sélectionnables ----
  // Ticket #45 — l'annuaire actif complet, pour tous les rôles : le besoin est
  // de contrôler les imputations de personnes hors de sa propre équipe qui
  // travaillent sur les mêmes affaires.
  useEffect(() => {
    apiPersons.list('active').then(({ items }) => setPersons(items)).catch(() => {});
  }, []);

  // Ticket #76 — la tâche entre dans l'identité d'une cellule : deux tâches de
  // la même affaire le même jour sont deux imputations distinctes.
  //
  // La clé sert UNIQUEMENT d'index. Rien ne la re-parse : tout ce dont le code
  // a besoin (affaire, tâche, date) est porté par la valeur de la cellule. Le
  // découpage positionnel qu'on faisait avant se serait décalé en silence en
  // ajoutant ce segment, et la date serait devenue un phaseId.
  const cellKey = (affaireId, phaseId, taskId, date) =>
    `${affaireId}|${phaseId ?? ''}|${taskId ?? ''}|${date}`;
  const rowKey = (affaireId, phaseId, taskId) =>
    `${affaireId}|${phaseId ?? ''}|${taskId ?? ''}`;

  const loadWeek = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { weekStart: toIsoDate(weekStart) };
      if (canSeeOthers && mode === 'admin' && selectedPersonId) {
        params.personId = selectedPersonId;
      }
      const resp = await apiLogtime.week(params);
      setData(resp);

      // Initialise les cells à partir des saisies existantes + suggestions.
      const next = {};

      // 1) LoggedTime existants : prioritaires (clé inclut phaseId).
      for (const day of resp.loggedTimesByDay) {
        for (const lt of day.items) {
          const key = cellKey(lt.affaireId, lt.phaseId, lt.taskId, day.date);
          next[key] = {
            hours: Number(lt.hours),
            notes: lt.notes ?? '',
            billable: lt.billable !== false,
            existingId: lt.id,
            phaseId: lt.phaseId ?? null,
            taskId: lt.taskId ?? null,
            task: lt.task ?? null,
            allocationId: lt.allocationId ?? null,
            affaireId: lt.affaireId,
            date: day.date,
            source: lt.source ?? 'manual',
          };
        }
      }

      // 2) Suggestions : ajoutées uniquement si pas de LoggedTime existant.
      for (const s of resp.suggestions ?? []) {
        const key = cellKey(s.affaireId, s.phaseId, s.taskId, s.date);
        if (next[key]) continue;
        next[key] = {
          hours: Number(s.hoursPerDay),
          notes: '',
          billable: s.billable !== false,
          existingId: null,
          phaseId: s.phaseId ?? null,
          taskId: s.taskId ?? null,
          task: s.task ?? null,
          allocationId: s.allocationId ?? null,
          affaireId: s.affaireId,
          date: s.date,
          suggestionId: s.suggestionId,
          isSuggestion: true,
          isLocked: Boolean(s.isLocked),
        };
      }

      setCells(next);
      setExtraAffaires([]);

      // Jours fériés (Sprint 5)
      try {
        const weekEndIso = resp.days[6]?.date;
        if (resp.person?.id && weekEndIso) {
          const { items: hols } = await apiPublicHolidays.forPerson(
            resp.person.id,
            resp.days[0].date,
            weekEndIso,
          );
          const map = new Map();
          for (const h of hols ?? []) {
            const iso = (h.date ?? '').slice(0, 10);
            if (iso) map.set(iso, h);
          }
          setHolidaysByDate(map);
        } else {
          setHolidaysByDate(new Map());
        }
      } catch {
        setHolidaysByDate(new Map());
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [weekStart, canSeeOthers, mode, selectedPersonId]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  // Ticket #52 — le mode Calendrier est rendu par un composant autonome qui
  // fait ses propres mutations ; ce composant-ci reste monté avec ses données
  // hebdo figées (`loadWeek` ne dépend pas de `viewMode`). Au retour sur le
  // Tableau, on recharge donc la semaine pour refléter les validations /
  // suppressions faites depuis le calendrier — sans quoi il fallait recharger
  // la page. Rechargement silencieux : `data` reste affiché pendant le fetch.
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    const previous = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    if (previous === 'calendar' && viewMode === 'table') {
      loadWeek();
    }
  }, [viewMode, loadWeek]);

  // ---- Congés par date (Sprint parité calendrier) ----
  // Map<iso, { full: timeOff[], partial: timeOff[] }>. Seul un congé APPROUVÉ
  // journée entière rend la cellule non saisissable (comme un férié) ; un congé
  // partiel — ou une demande encore en attente de validation — s'affiche en
  // annotation mais laisse la saisie possible (parité avec le Schedule, qui
  // affiche également les demandes en attente).
  const timeOffByDate = useMemo(() => {
    const m = new Map();
    if (!data?.timeOffsByDay) return m;
    for (const day of data.timeOffsByDay) {
      if (!day.timeOffs?.length) continue;
      const full = [];
      const partial = [];
      for (const t of day.timeOffs) {
        if (t.fullDay !== false && t.status === 'APPROVED') full.push(t);
        else partial.push(t);
      }
      m.set(day.date, { full, partial });
    }
    return m;
  }, [data]);

  // ---- Helpers lock ----
  // Une date est verrouillée si <= cutoff effectif ET non couverte par une
  // exemption temporaire (grant) applicable à cette Person. ADMIN → lock null
  // côté backend (bypass), donc lockMs null ⇒ jamais verrouillé.
  const lockDate = data?.lock?.lockDate ?? null;
  const lockMs = lockDate ? new Date(lockDate + 'T00:00:00Z').getTime() : null;
  const unlockGrants = data?.unlockGrants ?? [];
  const isDateGranted = (isoDate) => {
    const dMs = new Date(isoDate + 'T00:00:00Z').getTime();
    return unlockGrants.some((g) => {
      const f = new Date(g.fromDate + 'T00:00:00Z').getTime();
      const t = new Date(g.toDate + 'T00:00:00Z').getTime();
      return dMs >= f && dMs <= t;
    });
  };
  const isDateLocked = (isoDate) => {
    if (lockMs == null) return false;
    const dMs = new Date(isoDate + 'T00:00:00Z').getTime();
    if (dMs > lockMs) return false;
    return !isDateGranted(isoDate);
  };

  // ---- Liste des Affaires affichées (allocations + saisies + suggestions + extra) ----
  const affairesRows = useMemo(() => {
    if (!data) return [];
    const byKey = new Map();
    const upsert = (row) => {
      const key = rowKey(row.affaireId, row.phaseId, row.taskId);
      if (!byKey.has(key)) byKey.set(key, row);
    };

    // 1) Allocations
    for (const day of data.allocationsByDay) {
      for (const a of day.allocations) {
        upsert({
          affaireId: a.affaireId,
          affaire: a.affaire,
          phaseId: a.phaseId ?? null,
          phase: a.phase ?? null,
          taskId: a.taskId ?? null,
          // `a.name` : libellé libre d'avant la reprise en tâches partagées.
          task: a.task ?? (a.name ? { id: null, name: a.name } : null),
          plannedByDate: {},
          allocationId: a.id,
        });
      }
    }
    // Remplit plannedByDate
    for (const day of data.allocationsByDay) {
      for (const a of day.allocations) {
        const row = byKey.get(rowKey(a.affaireId, a.phaseId, a.taskId));
        if (row) row.plannedByDate[day.date] = Number(a.hoursPerDay);
      }
    }

    // 2) Saisies existantes
    for (const day of data.loggedTimesByDay) {
      for (const lt of day.items) {
        upsert({
          affaireId: lt.affaireId,
          affaire: lt.affaire,
          phaseId: lt.phaseId ?? null,
          phase: lt.phase ?? null,
          taskId: lt.taskId ?? null,
          task: lt.task ?? null,
          plannedByDate: {},
          allocationId: lt.allocationId ?? null,
        });
      }
    }

    // 3) Suggestions (sécurité : si une suggestion n'a pas matché une alloc présente)
    for (const s of data.suggestions ?? []) {
      upsert({
        affaireId: s.affaireId,
        affaire: s.affaire,
        phaseId: s.phaseId ?? null,
        phase: s.phase ?? null,
        taskId: s.taskId ?? null,
        task: s.task ?? null,
        plannedByDate: {},
        allocationId: s.allocationId ?? null,
      });
    }

    // 4) Affaires ajoutées manuellement
    for (const a of extraAffaires) {
      const key = rowKey(a.id, null, a.taskId ?? null);
      if (!byKey.has(key)) {
        byKey.set(key, {
          affaireId: a.id,
          affaire: { id: a.id, name: a.name, color: a.color },
          phaseId: null,
          phase: null,
          taskId: a.taskId ?? null,
          task: a.taskName ? { id: a.taskId ?? null, name: a.taskName } : null,
          // Une tâche ajoutée à la main n'existe pas encore côté serveur tant
          // qu'aucune heure n'est saisie : on retient le libellé à créer.
          pendingTaskName: a.taskId ? null : (a.taskName ?? null),
          plannedByDate: {},
          allocationId: null,
        });
      }
    }

    // Tri par affaire puis par tâche : les lignes d'une même affaire restent
    // groupées, ce qui est la lecture attendue d'une grille hebdo.
    return [...byKey.values()].sort(
      (x, y) =>
        (x.affaire?.name ?? '').localeCompare(y.affaire?.name ?? '') ||
        (x.task?.name ?? '').localeCompare(y.task?.name ?? '', 'fr', { sensitivity: 'base' }),
    );
  }, [data, extraAffaires]);

  // ---- Calculs totaux ----
  const draftTotalsByDate = useMemo(() => {
    const totals = {};
    if (!data) return totals;
    for (const day of data.days) totals[day.date] = 0;
    for (const val of Object.values(cells)) {
      // La date vient de la VALEUR, pas d'un découpage de la clé : c'est ce qui
      // rend l'ajout du segment `taskId` (#76) sans effet de bord ici.
      const date = val.date;
      if (!date) continue;
      // On compte uniquement les LoggedTime existants (les suggestions
      // pré-remplies ne sont pas encore comptées comme "loggées").
      if (val.existingId && Number.isFinite(val.hours) && val.hours > 0) {
        totals[date] = (totals[date] || 0) + val.hours;
      }
    }
    return totals;
  }, [cells, data]);

  const weekTotal = useMemo(
    () => Object.values(draftTotalsByDate).reduce((a, b) => a + b, 0),
    [draftTotalsByDate],
  );

  // ---- Navigation ----
  const shiftWeek = (deltaWeeks) => {
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + deltaWeeks * 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(getMonday(new Date()));

  // ---- Picker Affaire ----
  // Sprint 7.2 — `mineOnly: true` restreint aux affaires du user courant
  // (membre actif d'équipe OR PM OR a loggué). ADMIN bypass côté backend
  // et voit tout. On force un re-fetch à chaque ouverture pour récupérer
  // les nouveaux projets sans recharger la page.
  const openPicker = async () => {
    const { items } = await apiAffaires.list('active', null, { mineOnly: true });
    // Affaires en planification uniquement : imputations fermées → non proposées.
    const offered = (items ?? []).filter((a) => !a.planningOnly);
    setAvailableAffaires(offered);
    // synotis-support#8 — présélectionne la dernière affaire saisie.
    setPickerSelection(lastAffaireIn(offered));
    setPickerOpen(true);
  };
  const addAffaire = (a, task = { taskId: null, taskName: null }) => {
    const row = { ...a, taskId: task.taskId ?? null, taskName: task.taskName ?? null };
    rememberLastAffaire(a.id);
    setExtraAffaires((prev) =>
      // Le doublon se juge sur le couple (affaire, tâche) : la même affaire
      // peut légitimement apparaître sur deux tâches différentes.
      prev.some((x) => x.id === row.id && (x.taskId ?? null) === row.taskId)
        ? prev
        : [...prev, row],
    );
    setPickerSelection('');
    setPickerTask({ taskId: null, taskName: null });
    setPickerOpen(false);
  };

  // ---- #55 — Reprendre les affaires de la semaine précédente -------------
  //
  // On rouvre la semaine d'avant et on en reprend les LIGNES (affaire, tâche),
  // jamais les heures : personne ne veut voir des heures apparaître sans les
  // avoir saisies. Les lignes déjà présentes cette semaine — allocation,
  // suggestion ou saisie — sont ignorées, le doublon se jugeant sur le couple
  // (affaire, tâche) comme à l'ajout manuel.
  //
  // Les affaires passées en « planification uniquement » depuis sont écartées :
  // leurs imputations sont fermées, la ligne serait inutilisable.
  const [previousBusy, setPreviousBusy] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(false);

  // `withHours` : reprendre aussi les heures, jour pour jour (même jour de la
  // semaine). Les notes ne sont PAS recopiées — elles décrivent ce qui a été
  // fait ce jour-là, les dupliquer produirait un compte rendu faux.
  const reusePreviousWeek = async (withHours) => {
    if (!data) return;
    setPreviousBusy(true);
    setSaveError(null);
    try {
      const prev = new Date(weekStart);
      prev.setUTCDate(prev.getUTCDate() - 7);
      const params = { weekStart: toIsoDate(prev) };
      if (canSeeOthers && mode === 'admin' && selectedPersonId) params.personId = selectedPersonId;
      const resp = await apiLogtime.week(params);

      const candidates = new Map(); // `${affaireId}|${taskId}` → ligne à ajouter
      for (const day of resp.loggedTimesByDay ?? []) {
        for (const lt of day.items ?? []) {
          if (lt.affaire?.planningOnly) continue;
          const k = `${lt.affaireId}|${lt.taskId ?? ''}`;
          if (!candidates.has(k)) {
            candidates.set(k, {
              id: lt.affaireId,
              name: lt.affaire?.name ?? '—',
              color: lt.affaire?.color ?? null,
              taskId: lt.taskId ?? null,
              taskName: lt.task?.name ?? null,
            });
          }
        }
      }

      const present = new Set(
        affairesRows.map((r) => `${r.affaireId}|${r.taskId ?? ''}`),
      );
      const toAdd = [...candidates.entries()]
        .filter(([k]) => !present.has(k))
        .map(([, row]) => row);

      if (candidates.size === 0) {
        setSaveError('Aucune saisie la semaine précédente : rien à reprendre.');
        setPreviousOpen(false);
        return;
      }
      if (toAdd.length > 0) setExtraAffaires((p) => [...p, ...toAdd]);

      if (!withHours) {
        if (toAdd.length === 0) {
          setSaveError('Les affaires de la semaine précédente sont déjà toutes présentes.');
        }
        setPreviousOpen(false);
        return;
      }

      // ---- Reprise des heures ------------------------------------------
      //
      // Chaque saisie est reportée sur le MÊME jour de la semaine. On écarte
      // ce que le serveur refuserait ou ce qui écraserait du travail :
      // journée verrouillée, férié, congé pleine journée, affaire fermée aux
      // imputations, et toute cellule déjà saisie cette semaine.
      const personId = data.person.id;
      const jours = data.days.map((d) => d.date);
      const plan = [];
      const ignores = { existantes: 0, verrouillees: 0, feriees: 0, conges: 0 };

      (resp.days ?? []).forEach((day, index) => {
        const cible = jours[index];
        if (!cible) return;
        const items = (resp.loggedTimesByDay?.[index]?.items ?? []).filter(
          (x) => !x.affaire?.planningOnly,
        );
        for (const x of items) {
          if (isDateLocked(cible)) { ignores.verrouillees += 1; continue; }
          if (holidaysByDate.get(cible)) { ignores.feriees += 1; continue; }
          if (timeOffByDate.get(cible)?.full?.length) { ignores.conges += 1; continue; }
          const k = cellKey(x.affaireId, x.phaseId ?? null, x.taskId ?? null, cible);
          if (cellsRef.current[k]?.existingId) { ignores.existantes += 1; continue; }
          plan.push({ cible, x, key: k });
        }
      });

      let crees = 0;
      for (const { cible, x, key } of plan) {
        try {
          const created = await apiLoggedTime.create({
            personId,
            affaireId: x.affaireId,
            date: cible,
            hours: Number(x.hours),
            billable: x.billable !== false,
            source: 'manual',
            ...(x.phaseId ? { phaseId: x.phaseId } : {}),
            ...(x.taskId ? { taskId: x.taskId } : {}),
          });
          crees += 1;
          setCells((st) => ({
            ...st,
            [key]: {
              ...(st[key] ?? {}),
              existingId: created.id,
              hours: Number(created.hours),
              notes: '',
              billable: created.billable !== false,
              affaireId: x.affaireId,
              date: cible,
              phaseId: x.phaseId ?? null,
              taskId: x.taskId ?? null,
              isSuggestion: false,
              source: created.source ?? 'manual',
            },
          }));
        } catch (e) {
          setSaveError(e.message);
          break;
        }
      }

      const ecartes = Object.entries({
        'déjà saisie(s)': ignores.existantes,
        'jour verrouillé': ignores.verrouillees,
        'jour férié': ignores.feriees,
        'congé': ignores.conges,
      })
        .filter(([, n]) => n > 0)
        .map(([l, n]) => `${n} ${l}`);
      if (crees === 0 && ecartes.length > 0) {
        setSaveError(`Aucune heure reprise — ${ecartes.join(', ')}.`);
      } else if (ecartes.length > 0) {
        setSaveError(`${crees} saisie(s) reprise(s) ; écartée(s) : ${ecartes.join(', ')}.`);
      }
      setPreviousOpen(false);
      await loadWeek();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setPreviousBusy(false);
    }
  };

  // ---- Sauvegarde unitaire d'une cellule (sur blur DurationInput) ----
  const doPersistCell = useCallback(
    async (row, date, newHours) => {
      if (!data) return;
      const personId = data.person.id;
      const key = cellKey(row.affaireId, row.phaseId, row.taskId, date);
      const cur = cellsRef.current[key] ?? {};

      // Durée minimale de saisie : une valeur strictement positive sous le seuil
      // est refusée côté UI (le backend reste l'autorité). 0/null = suppression.
      if (Number.isFinite(newHours) && newHours > 0 && minHours > 0 && newHours < minHours) {
        setSaveError(`Durée minimale de saisie : ${formatHHMM(minHours)} (saisie de ${formatHHMM(newHours)} refusée)`);
        return;
      }

      setBusyCells((s) => ({ ...s, [key]: true }));
      setSaveError(null);
      try {
        if (cur.existingId) {
          // PATCH existant. Si newHours est 0 ou null → archive.
          if (!Number.isFinite(newHours) || newHours <= 0) {
            await apiLoggedTime.archive(cur.existingId);
            setCells((s) => {
              const out = { ...s };
              delete out[key];
              return out;
            });
          } else {
            const updated = await apiLoggedTime.update(cur.existingId, {
              hours: newHours,
              notes: cur.notes || null,
              billable: cur.billable !== false,
            });
            setCells((s) => ({
              ...s,
              [key]: {
                ...cur,
                hours: Number(updated.hours),
                source: updated.source ?? cur.source ?? 'manual',
              },
            }));
          }
        } else if (Number.isFinite(newHours) && newHours > 0) {
          // CREATE (saisie manuelle hors planning OU édition d'une suggestion
          // qui devient un vrai LoggedTime via input direct).
          const created = await apiLoggedTime.create({
            personId,
            affaireId: row.affaireId,
            date,
            hours: newHours,
            notes: cur.notes || null,
            billable: cur.billable !== false,
            // Source 'planning' si la cellule était une suggestion, sinon 'manual'.
            source: cur.isSuggestion ? 'planning' : 'manual',
            ...(cur.phaseId ? { phaseId: cur.phaseId } : {}),
            // Ticket #76 — `taskId` pour une tâche du référentiel ;
            // `pendingTaskName` pour une ligne ajoutée à la main dont la tâche
            // n'existe pas encore : le serveur la crée à la première saisie.
            ...(row.taskId
              ? { taskId: row.taskId }
              : row.pendingTaskName
              ? { taskName: row.pendingTaskName }
              : {}),
            ...(cur.allocationId ? { allocationId: cur.allocationId } : {}),
          });
          setCells((s) => ({
            ...s,
            [key]: {
              ...cur,
              existingId: created.id,
              hours: Number(created.hours),
              isSuggestion: false,
              source: created.source ?? 'manual',
            },
          }));
        }
      } catch (e) {
        setSaveError(e.message);
      } finally {
        setBusyCells((s) => {
          const out = { ...s };
          delete out[key];
          return out;
        });
      }
    },
    [data, minHours],
  );

  const persistCell = useCallback(
    (row, date, newHours) => {
      if (!data) return undefined;
      const key = cellKey(row.affaireId, row.phaseId, row.taskId, date);
      // Sérialisation par cellule : la saisie N+1 attend la fin de la N.
      const next = (pendingRef.current[key] ?? Promise.resolve()).then(() =>
        doPersistCell(row, date, newHours),
      );
      pendingRef.current[key] = next.catch(() => {});
      return next;
    },
    [data, doPersistCell],
  );


  // ---- #90 — Note par cellule -------------------------------------------
  //
  // La note vit sur le `LoggedTime` : elle n'est éditable que sur une cellule
  // déjà saisie. Sur une suggestion ou une case vide, il n'y a rien à annoter
  // tant que les heures ne sont pas posées — le bouton reste alors masqué.
  const [noteCell, setNoteCell] = useState(null); // { key, row, date, value }
  const [noteBusy, setNoteBusy] = useState(false);

  const saveNote = async () => {
    if (!noteCell) return;
    const { key, value } = noteCell;
    const cur = cellsRef.current[key];
    if (!cur?.existingId) {
      setNoteCell(null);
      return;
    }
    setNoteBusy(true);
    setSaveError(null);
    try {
      const notes = value.trim() || null;
      await apiLoggedTime.update(cur.existingId, { notes });
      setCells((st) => ({ ...st, [key]: { ...st[key], notes: notes ?? '' } }));
      setNoteCell(null);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setNoteBusy(false);
    }
  };

  // ---- Tâche par jour (#90, #76) ----------------------------------------
  //
  // La tâche se choisit sur la CELLULE, pas sur la ligne : deux jours d'une
  // même allocation peuvent porter des tâches différentes. La ligne étant
  // identifiée par `affaireId|phaseId|taskId`, la saisie changée de tâche
  // rejoint après rechargement la ligne de sa nouvelle tâche.
  //
  // - saisie existante : on change la tâche de la saisie ;
  // - suggestion (heures prévues non saisies) : on saisit les heures prévues
  //   sur la tâche choisie. Une tâche n'existe que portée par une saisie.
  const [taskCell, setTaskCell] = useState(null); // { row, date, cell, value }
  const [taskBusy, setTaskBusy] = useState(false);

  const saveCellTask = async () => {
    if (!taskCell || !data?.person) return;
    const { row, date, cell, value } = taskCell;
    const taskPayload = value.taskId
      ? { taskId: value.taskId }
      : value.taskName
      ? { taskName: value.taskName }
      : { taskId: null };
    setTaskBusy(true);
    setSaveError(null);
    try {
      if (cell.existingId) {
        await apiLoggedTime.update(cell.existingId, taskPayload);
      } else {
        await apiLoggedTime.create({
          personId: data.person.id,
          affaireId: row.affaireId,
          date,
          hours: cell.hours,
          notes: null,
          billable: cell.billable !== false,
          source: 'planning',
          ...(cell.phaseId ? { phaseId: cell.phaseId } : {}),
          ...(cell.allocationId ? { allocationId: cell.allocationId } : {}),
          ...(value.taskId || value.taskName ? taskPayload : {}),
        });
      }
      setTaskCell(null);
      await loadWeek();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setTaskBusy(false);
    }
  };

  // ---- Suppression d'une ligne entière ----------------------------------
  //
  // Archive toutes les saisies de la ligne sur la semaine affichée, sauf les
  // journées verrouillées (le serveur les refuserait). Une ligne issue d'une
  // allocation reste affichée après coup : le planning n'est pas modifié,
  // seules les imputations le sont — les suggestions réapparaissent donc.
  const [deleteRow, setDeleteRow] = useState(null); // row
  const [deleteRowBusy, setDeleteRowBusy] = useState(false);

  const rowDeletableEntries = (row) =>
    (data?.days ?? [])
      .filter((d) => !isDateLocked(d.date))
      .map((d) => cellsRef.current[cellKey(row.affaireId, row.phaseId, row.taskId, d.date)])
      .filter((c) => c?.existingId);

  const confirmDeleteRow = async () => {
    if (!deleteRow) return;
    const row = deleteRow;
    setDeleteRowBusy(true);
    setSaveError(null);
    try {
      for (const c of rowDeletableEntries(row)) {
        await apiLoggedTime.archive(c.existingId);
      }
      setExtraAffaires((prev) =>
        prev.filter(
          (a) => !(a.id === row.affaireId && (a.taskId ?? null) === (row.taskId ?? null)),
        ),
      );
      setDeleteRow(null);
      await loadWeek();
    } catch (e) {
      setSaveError(e.message);
      setDeleteRow(null);
      await loadWeek();
    } finally {
      setDeleteRowBusy(false);
    }
  };

  // ---- "Log all" : POST /logged-time/bulk avec toutes les suggestions ----
  const logAllSuggestions = async () => {
    if (!data || !data.person) return;
    const personId = data.person.id;
    // Chaque cellule porte son affaire, sa tâche et sa date : une seule
    // itération suffit, sans redécouper la clé (ticket #76).
    const batch = [];
    for (const val of Object.values(cells)) {
      if (!val.isSuggestion) continue;
      if (val.isLocked) continue;
      if (!Number.isFinite(val.hours) || val.hours <= 0) continue;
      batch.push({
        personId,
        affaireId: val.affaireId,
        date: val.date,
        hours: val.hours,
        billable: val.billable !== false,
        source: 'planning',
        ...(val.phaseId ? { phaseId: val.phaseId } : {}),
        ...(val.taskId ? { taskId: val.taskId } : {}),
        ...(val.allocationId ? { allocationId: val.allocationId } : {}),
      });
    }
    if (batch.length === 0) return;
    setBulkBusy(true);
    setSaveError(null);
    try {
      await apiLoggedTime.bulkCreate(batch);
      await loadWeek();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- "Log" par ligne : bulk uniquement sur les suggestions de la ligne ----
  const logRowSuggestions = async (row) => {
    if (!data || !data.person) return;
    const personId = data.person.id;
    const batch = [];
    for (const day of data.days) {
      const key = cellKey(row.affaireId, row.phaseId, row.taskId, day.date);
      const val = cells[key];
      if (!val?.isSuggestion) continue;
      if (val.isLocked) continue;
      if (!Number.isFinite(val.hours) || val.hours <= 0) continue;
      batch.push({
        personId,
        affaireId: row.affaireId,
        date: day.date,
        hours: val.hours,
        billable: val.billable !== false,
        source: 'planning',
        ...(val.phaseId ? { phaseId: val.phaseId } : {}),
        ...(val.taskId ? { taskId: val.taskId } : {}),
        ...(val.allocationId ? { allocationId: val.allocationId } : {}),
      });
    }
    if (batch.length === 0) return;
    setBulkBusy(true);
    setSaveError(null);
    try {
      await apiLoggedTime.bulkCreate(batch);
      await loadWeek();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- Total des suggestions actives (non lockées, non encore loggées) ----
  const totalSuggestedActive = useMemo(() => {
    let total = 0;
    for (const val of Object.values(cells)) {
      if (!val.isSuggestion) continue;
      if (val.isLocked) continue;
      if (Number.isFinite(val.hours) && val.hours > 0) total += val.hours;
    }
    return total;
  }, [cells]);

  // ---- Compteur suggestions par ligne ----
  const rowSuggestedTotal = (row) => {
    if (!data) return 0;
    let total = 0;
    for (const day of data.days) {
      const key = cellKey(row.affaireId, row.phaseId, row.taskId, day.date);
      const v = cells[key];
      if (!v?.isSuggestion) continue;
      if (v.isLocked) continue;
      if (Number.isFinite(v.hours) && v.hours > 0) total += v.hours;
    }
    return total;
  };

  // ---- Render ----
  // Sprint 12 — mode Calendrier : composant autonome (charge ses propres
  // données via /api/logtime/calendar). Placé après tous les hooks.
  if (viewMode === 'calendar') {
    return (
      <LogtimeCalendarView
        user={user}
        viewMode={viewMode}
        onChangeViewMode={setViewMode}
        initialDate={initialDate}
      />
    );
  }

  if (loading && !data) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;
  }
  if (error) {
    return <div style={{ padding: 32, color: 'var(--danger)' }}>{error}</div>;
  }
  if (!data) return null;

  const weekEnd = data.days[6]?.date;
  const hasLock = Boolean(data.lock);
  const isCurrentWeekLocked = Boolean(data.isCurrentWeekLocked);
  // Sprint 9 — MANAGER en mode équipe : pas de bulk (lecture seule).
  // Ticket #45 — le serveur dit si la semaine affichée est saisissable
  // (`canEdit: false` dès qu'on consulte quelqu'un d'autre sans droit d'écriture).
  const readOnlyWeek = data?.canEdit === false;
  const logAllDisabled = bulkBusy || totalSuggestedActive <= 0 || readOnlyWeek;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1500, margin: '0 auto' }}>
      {/* Bandeau verrou */}
      {hasLock ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: 'var(--glass)',
            border: '1px solid var(--warning)',
            color: 'var(--warning)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <Lock size={14} />
          <span>
            Période verrouillée jusqu'au <strong>{data.lock.lockDate}</strong>
            {data.lock.reason ? ` — ${data.lock.reason}` : ''}
            . Les saisies de cette période sont en lecture seule.
          </span>
        </div>
      ) : null}

      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Saisie de temps</h1>

        {/* Sprint 12 — bascule Tableau | Calendrier */}
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            style={{
              ...pillBtn,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: viewMode === 'table' ? 'var(--accent)' : 'var(--glass)',
              color: viewMode === 'table' ? '#fff' : 'var(--text)',
            }}
          >
            <Table size={13} /> Tableau
          </button>
          <button
            type="button"
            onClick={() => setViewMode('calendar')}
            style={{
              ...pillBtn,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: viewMode === 'calendar' ? 'var(--accent)' : 'var(--glass)',
              color: viewMode === 'calendar' ? '#fff' : 'var(--text)',
            }}
          >
            <CalendarDays size={13} /> Calendrier
          </button>
        </div>

        {canSeeOthers ? (
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setMode('self')}
              style={{
                ...pillBtn,
                background: mode === 'self' ? 'var(--primary)' : 'var(--glass)',
                color: mode === 'self' ? '#fff' : 'var(--text)',
              }}
            >
              Ma saisie
            </button>
            <button
              type="button"
              onClick={() => setMode('admin')}
              style={{
                ...pillBtn,
                background: mode === 'admin' ? 'var(--primary)' : 'var(--glass)',
                color: mode === 'admin' ? '#fff' : 'var(--text)',
              }}
            >
              {othersLabel}
            </button>
          </div>
        ) : null}

        {canSeeOthers && mode === 'admin' ? (
          <select
            value={selectedPersonId ?? ''}
            onChange={(e) => setSelectedPersonId(e.target.value || null)}
            style={{ ...inputStyle, width: 240, textAlign: 'left' }}
          >
            <option value="">— Sélectionner une personne —</option>
            {persons.map((p) => (
              <option key={p.id} value={p.id}>
                {p.lastName} {p.firstName}
              </option>
            ))}
          </select>
        ) : null}

        {readOnlyWeek ? (
          <span
            title="Vous consultez la semaine d'une autre personne : la saisie est désactivée."
            style={{
              ...pillBtn,
              cursor: 'default',
              background: 'var(--glass)',
              color: 'var(--text-muted)',
              border: '1px solid var(--glass-border)',
            }}
          >
            Lecture seule
          </span>
        ) : null}

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button type="button" onClick={() => shiftWeek(-1)} style={secondaryBtn} title="Semaine précédente">
            <ChevronLeft size={14} />
          </button>
          <button type="button" onClick={goToday} style={secondaryBtn}>
            <CalendarDays size={14} /> Aujourd'hui
          </button>
          <button type="button" onClick={() => shiftWeek(1)} style={secondaryBtn} title="Semaine suivante">
            <ChevronRight size={14} />
          </button>
          {/* Largeur FIXE : un libellé de largeur variable décalait les flèches
              et « Aujourd'hui » à chaque clic → clic par erreur. */}
          <span
            style={{
              color: 'var(--text-muted)',
              fontSize: 13,
              width: '15.5em',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            Semaine du {formatRange(data.days[0].date, weekEnd)}
          </span>
          <button type="button" onClick={openPicker} style={secondaryBtn}>
            <Plus size={14} /> Ajouter une affaire
          </button>
          {/* #55 — repartir des lignes de la semaine précédente (sans les heures). */}
          <button
            type="button"
            onClick={() => setPreviousOpen(true)}
            disabled={previousBusy || readOnlyWeek}
            title="Reprendre les affaires de la semaine précédente, avec ou sans les heures"
            style={{ ...secondaryBtn, opacity: previousBusy || readOnlyWeek ? 0.5 : 1 }}
          >
            <CopyPlus size={14} /> {previousBusy ? 'Reprise…' : 'Reprendre la semaine précédente'}
          </button>
          <button
            type="button"
            onClick={logAllSuggestions}
            disabled={logAllDisabled}
            title={
              isCurrentWeekLocked
                ? 'Certains jours de la semaine sont verrouillés — seules les suggestions hors lock sont prises en compte.'
                : totalSuggestedActive <= 0
                ? 'Aucune suggestion à valider'
                : `Valider ${formatHHMM(totalSuggestedActive)} en une fois`
            }
            style={{
              ...primaryBtn,
              opacity: logAllDisabled ? 0.5 : 1,
              cursor: logAllDisabled ? 'not-allowed' : 'pointer',
              // Largeur stable d'une semaine à l'autre (bloc aligné à droite :
              // sinon les flèches de navigation bougent).
              minWidth: '8em',
              justifyContent: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Zap size={14} />
            Log {formatHHMM(totalSuggestedActive)}
          </button>
        </div>
      </div>

      {saveError ? (
        <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{saveError}</div>
      ) : null}

      {data.person ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Avatar person={data.person} size={28} title={`${data.person.firstName} ${data.person.lastName}`} />
          <span>Personne :{' '}
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setEditPersonId(data.person.id)}
              title={`Modifier ${data.person.firstName} ${data.person.lastName}`}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                font: 'inherit',
                fontWeight: 700,
                color: 'var(--accent)',
                cursor: 'pointer',
              }}
            >
              {data.person.firstName} {data.person.lastName}
            </button>
          ) : (
            <strong>{data.person.firstName} {data.person.lastName}</strong>
          )}{' '}
          ({data.person.email ?? 'sans email'})
          {holidaysByDate.size > 0 ? (
            <span style={{ marginLeft: 12, color: 'var(--warning)' }}>
              • {holidaysByDate.size} jour{holidaysByDate.size > 1 ? 's' : ''} férié{holidaysByDate.size > 1 ? 's' : ''} cette semaine
            </span>
          ) : null}
          </span>
        </div>
      ) : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflowX: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, minWidth: 240 }}>Affaire</th>
              {data.days.map((d, idx) => (
                <th
                  key={d.date}
                  style={{
                    padding: '10px 8px',
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    textAlign: 'center',
                    minWidth: 110,
                  }}
                >
                  <div>{DAY_LABELS[idx]}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                  </div>
                </th>
              ))}
              <th style={{ padding: '10px 8px', color: 'var(--text-muted)', fontWeight: 500, textAlign: 'center' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {affairesRows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucune allocation cette semaine. Cliquez sur « Ajouter une affaire ».
                </td>
              </tr>
            ) : (
              affairesRows.map((row) => {
                const rowTotal = data.days.reduce((acc, d) => {
                  const key = cellKey(row.affaireId, row.phaseId, row.taskId, d.date);
                  const v = cells[key];
                  if (v?.existingId && Number.isFinite(v.hours)) return acc + v.hours;
                  return acc;
                }, 0);
                const rowSuggest = rowSuggestedTotal(row);
                return (
                  <tr
                    key={rowKey(row.affaireId, row.phaseId, row.taskId) + (row.task?.name ?? '')}
                    style={{ borderTop: '1px solid var(--glass-border)' }}
                  >
                    <td style={{ padding: '8px 12px', color: 'var(--text)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: 4,
                            background: row.affaire?.color ? `#${row.affaire.color}` : 'var(--glass)',
                            border: '1px solid var(--glass-border)',
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{row.affaire?.name ?? '—'}</span>
                          {row.task?.name ? (
                            <span style={{ fontSize: 11, color: 'var(--accent)' }}>
                              {row.task.name}
                            </span>
                          ) : null}
                          {row.phase?.name ? (
                            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                              Phase : {row.phase.name}
                            </span>
                          ) : null}
                          {row.affaire?.planningOnly ? (
                            <span
                              title="Affaire en planification uniquement : imputations fermées tant que la proposition n'est pas gagnée"
                              style={{
                                alignSelf: 'flex-start',
                                marginTop: 2,
                                padding: '1px 8px',
                                borderRadius: 'var(--radius-pill)',
                                border: '1px solid var(--warning)',
                                color: 'var(--warning)',
                                fontSize: 10,
                              }}
                            >
                              Planification
                            </span>
                          ) : null}
                        </div>
                        {!readOnlyWeek &&
                        (rowDeletableEntries(row).length > 0 ||
                          (!row.allocationId && rowTotal === 0 && rowSuggest === 0)) ? (
                          <button
                            type="button"
                            onClick={() => setDeleteRow(row)}
                            title="Supprimer la ligne (toutes les saisies de la semaine)"
                            aria-label="Supprimer la ligne"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 2,
                              cursor: 'pointer',
                              color: 'var(--danger)',
                              display: 'inline-flex',
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        ) : null}
                        {rowSuggest > 0 ? (
                          <button
                            type="button"
                            onClick={() => logRowSuggestions(row)}
                            disabled={bulkBusy}
                            style={{ ...rowLogBtn, marginLeft: 'auto', opacity: bulkBusy ? 0.5 : 1 }}
                            title={`Valider ${formatHHMM(rowSuggest)} pour cette affaire`}
                          >
                            Log {formatHHMM(rowSuggest)}
                          </button>
                        ) : null}
                      </div>
                    </td>
                    {data.days.map((d) => {
                      const key = cellKey(row.affaireId, row.phaseId, row.taskId, d.date);
                      const cell = cells[key];
                      const planned = row.plannedByDate[d.date];
                      const holiday = holidaysByDate.get(d.date);
                      const isHoliday = Boolean(holiday);
                      const isLockedDate = isDateLocked(d.date);
                      // Congés (parité calendrier) : journée entière =
                      // non saisissable ; partiel = annotation, saisie possible.
                      const off = timeOffByDate.get(d.date);
                      const fullDayOff = Boolean(off?.full?.length);
                      const partialOff = off?.partial ?? [];
                      const fullDayOffType = fullDayOff ? off.full[0].timeOffType : null;
                      // Ticket #29 — le MANAGER peut saisir/modifier les
                      // imputations de ses subordonnés directs (décision produit
                      // 2026-07) ; ticket #45 — consulter quelqu'un d'autre est
                      // ouvert à tous, mais en lecture seule. L'autorité est le
                      // serveur, qui tranche via `canEdit` (canManagePerson).
                      // Le bulk « Log all » reste désactivé hors de sa propre
                      // semaine via logAllDisabled.
                      // Affaire en planification uniquement : imputations fermées.
                      const planningOnly = Boolean(row.affaire?.planningOnly);
                      const isReadOnly =
                        isHoliday || fullDayOff || isLockedDate || readOnlyWeek || planningOnly;

                      const cellBg = isHoliday
                        ? 'repeating-linear-gradient(45deg, rgba(0,0,0,0.05), rgba(0,0,0,0.05) 4px, transparent 4px, transparent 8px)'
                        : fullDayOff
                        ? 'repeating-linear-gradient(45deg, rgba(50,100,150,0.10), rgba(50,100,150,0.10) 4px, transparent 4px, transparent 8px)'
                        : isLockedDate
                        ? 'rgba(255, 200, 100, 0.06)'
                        : undefined;

                      const isSuggestion = cell?.isSuggestion;
                      const isExisting = Boolean(cell?.existingId);
                      const isPlanning = cell?.source === 'planning';

                      const cellBorder = isSuggestion
                        ? '1px solid var(--warning)'
                        : isExisting
                        ? '1px solid var(--success)'
                        : '1px solid var(--glass-border)';

                      return (
                        <td
                          key={d.date}
                          style={{
                            padding: '6px 8px',
                            verticalAlign: 'top',
                            ...(cellBg ? { background: cellBg } : {}),
                          }}
                          title={
                            isHoliday
                              ? `Férié — ${holiday.name}${holiday.regionCode ? ' (régional)' : ' (national)'}`
                              : fullDayOff
                              ? `Congé — ${fullDayOffType?.name ?? ''} (journée entière)`
                              : isLockedDate
                              ? `Période verrouillée jusqu'au ${data.lock?.lockDate ?? ''}`
                              : planningOnly
                              ? 'Affaire en planification uniquement : imputations fermées'
                              : undefined
                          }
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <DurationInput
                              value={isHoliday || fullDayOff ? 0 : (cell?.hours ?? null)}
                              onChange={(v) => persistCell(row, d.date, v)}
                              readOnly={isReadOnly}
                              ariaLabel={`${row.affaire?.name ?? ''} ${d.date}`}
                              style={{
                                width: '100%',
                                border: cellBorder,
                                // Ticket #43 — la sauvegarde en cours est signalée par
                                // l'opacité, PAS par `readOnly` : un champ readOnly
                                // ignore `onChange`/`onBlur` et avalait la frappe
                                // suivante pendant l'aller-retour réseau.
                                opacity: isReadOnly ? 0.6 : busyCells[key] ? 0.7 : 1,
                              }}
                            />
                            {isSuggestion ? (
                              <span
                                style={{
                                  fontSize: 9,
                                  color: 'var(--warning)',
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.3,
                                  fontWeight: 600,
                                }}
                              >
                                Suggéré
                              </span>
                            ) : isExisting && isPlanning ? (
                              <span
                                style={{
                                  fontSize: 9,
                                  color: 'var(--text-muted)',
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.3,
                                }}
                              >
                                Planifié
                              </span>
                            ) : null}
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {/* Tâche du jour : sur une saisie ou sur des heures
                                prévues (la choisir saisit alors ces heures). */}
                            {(cell?.existingId || (cell?.isSuggestion && cell.hours > 0)) &&
                            !isReadOnly ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setTaskCell({
                                    row,
                                    date: d.date,
                                    cell,
                                    value: {
                                      taskId: row.taskId ?? null,
                                      taskName: row.task?.name ?? row.pendingTaskName ?? null,
                                    },
                                  })
                                }
                                title={
                                  row.task?.name
                                    ? `Tâche du jour : ${row.task.name} — cliquer pour changer`
                                    : 'Choisir la tâche de ce jour'
                                }
                                aria-label="Tâche du jour"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  color: row.task?.name ? 'var(--accent)' : 'var(--text-muted)',
                                  display: 'inline-flex',
                                }}
                              >
                                <Tag size={13} />
                              </button>
                            ) : null}
                            {/* #90 — note du jour : uniquement sur une saisie
                                existante, la note étant portée par le LoggedTime. */}
                            {cell?.existingId && !isReadOnly ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setNoteCell({ key, row, date: d.date, value: cell.notes ?? '' })
                                }
                                title={cell.notes ? cell.notes : 'Ajouter une note'}
                                aria-label={cell.notes ? 'Modifier la note' : 'Ajouter une note'}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  color: cell.notes ? 'var(--accent)' : 'var(--text-muted)',
                                  opacity: cell.notes ? 1 : 0.55,
                                  display: 'inline-flex',
                                }}
                              >
                                <MessageSquare size={12} />
                              </button>
                            ) : null}
                            </div>
                            {isHoliday ? (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: 'var(--warning)',
                                  textAlign: 'center',
                                  fontWeight: 500,
                                }}
                              >
                                Férié — {holiday.name}
                              </div>
                            ) : fullDayOff ? (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: 'var(--accent)',
                                  textAlign: 'center',
                                  fontWeight: 500,
                                }}
                              >
                                Congé — {fullDayOffType?.name ?? ''}
                              </div>
                            ) : planned && !isExisting && !isSuggestion ? (
                              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                Planifié : {planned}h
                              </div>
                            ) : null}
                            {/* Congé partiel : annotation sans bloquer la saisie. */}
                            {!fullDayOff && partialOff.length > 0 ? (
                              <div
                                style={{
                                  fontSize: 9,
                                  color: 'var(--accent)',
                                  textAlign: 'center',
                                  fontWeight: 500,
                                }}
                                title={partialOff
                                  .map(
                                    (t) =>
                                      `${t.timeOffType?.name ?? 'Congé'} — ${
                                        t.fullDay !== false
                                          ? 'journée entière'
                                          : formatHHMM(Number(t.hoursPerDay))
                                      }${t.status === 'PENDING' ? ' (en attente de validation)' : ''}`,
                                  )
                                  .join('\n')}
                              >
                                {partialOff.some((t) => t.fullDay !== false)
                                  ? `Congé ${
                                      partialOff.find((t) => t.fullDay !== false)?.timeOffType?.name ??
                                      ''
                                    }`.trim()
                                  : `Congé partiel ${formatHHMM(
                                      partialOff.reduce(
                                        (acc, t) => acc + Number(t.hoursPerDay || 0),
                                        0,
                                      ),
                                    )}`}
                                {partialOff.every((t) => t.status === 'PENDING') ? ' • en attente' : ''}
                              </div>
                            ) : null}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding: '8px 8px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 500 }}>
                      {rowTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })
            )}
            <tr style={{ borderTop: '2px solid var(--glass-border)', background: 'var(--glass)' }}>
              <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 600 }}>Total / jour (loggé)</td>
              {data.days.map((d) => {
                const dayTotal = draftTotalsByDate[d.date] ?? 0;
                const over = dayTotal > 8;
                return (
                  <td
                    key={d.date}
                    title={over ? `Dépassement : +${(dayTotal - 8).toFixed(2)}h au-delà de 8h` : undefined}
                    style={{
                      padding: '10px 8px',
                      textAlign: 'center',
                      color: over ? 'var(--danger)' : 'var(--text)',
                      fontWeight: over ? 800 : 600,
                      background: over ? 'rgba(220, 38, 38, 0.14)' : undefined,
                      borderTop: over ? '2px solid var(--danger)' : undefined,
                    }}
                  >
                    {dayTotal.toFixed(2)}
                    {over ? (
                      <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--danger)' }}>
                        +{(dayTotal - 8).toFixed(2)}
                      </span>
                    ) : null}
                  </td>
                );
              })}
              <td style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--text)', fontWeight: 700 }}>
                {weekTotal.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Ajouter une affaire"
        size="lg"
        fill
        footer={
          <>
            <button type="button" onClick={() => setPickerOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              disabled={!pickerSelection}
              onClick={() => {
                const a = availableAffaires.find((x) => x.id === pickerSelection);
                if (a) addAffaire(a, pickerTask);
              }}
              style={{
                ...secondaryBtn,
                background: pickerSelection ? 'var(--accent)' : 'var(--glass)',
                color: pickerSelection ? '#fff' : 'var(--text-muted)',
                cursor: pickerSelection ? 'pointer' : 'not-allowed',
              }}
            >
              Ajouter
            </button>
          </>
        }
      >
        {/* Le picker est rendu en `inline` : son panneau occupe toute la
            hauteur disponible et porte le seul scroll de la modale, header et
            footer restant fixes (ticket #51). Remplace le `minHeight: 360` du
            ticket #14, qui réservait la place du dropdown ancré et créait un
            double scroll dès que la liste était longue. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            flex: 1,
            minHeight: 0,
          }}
        >
          {availableAffaires.length === 0 ? (
            <div style={{ color: 'var(--text-muted)' }}>
              Aucune affaire disponible. Demande à ton admin de t'ajouter à
              une équipe projet, ou contacte le PM concerné.
            </div>
          ) : (
            <AffaireFilterableSelect
              id="logtime-add-affaire"
              affaires={availableAffaires}
              value={pickerSelection}
              onChange={(next) => {
                setPickerSelection(next);
                setPickerTask({ taskId: null, taskName: null });
              }}
              inline
              autoFocus
              selectStyle={{
                width: '100%',
                padding: '8px 10px',
                background: 'var(--glass)',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 14,
              }}
            />
          )}

          {pickerSelection ? (
            <div>
              <label
                htmlFor="logtime-add-task"
                style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}
              >
                Tâche (optionnel)
              </label>
              <TaskPicker
                inputId="logtime-add-task"
                affaireId={pickerSelection}
                value={pickerTask}
                onChange={setPickerTask}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'var(--glass)',
                  color: 'var(--text)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 14,
                }}
              />
            </div>
          ) : null}
        </div>
      </Modal>

      {/* #55 — choix du périmètre de la reprise */}
      <Modal
        open={previousOpen}
        onClose={() => (previousBusy ? undefined : setPreviousOpen(false))}
        title="Reprendre la semaine précédente"
        footer={
          <button
            type="button"
            onClick={() => setPreviousOpen(false)}
            disabled={previousBusy}
            style={secondaryBtn}
          >
            Annuler
          </button>
        }
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Que faut-il reprendre de la semaine précédente ?
          </div>
          <button
            type="button"
            onClick={() => reusePreviousWeek(false)}
            disabled={previousBusy}
            style={{ ...choiceBtn, opacity: previousBusy ? 0.6 : 1 }}
          >
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>Les affaires seulement</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Prépare les lignes (affaire et tâche), la saisie des heures reste à faire.
            </span>
          </button>
          <button
            type="button"
            onClick={() => reusePreviousWeek(true)}
            disabled={previousBusy}
            style={{ ...choiceBtn, opacity: previousBusy ? 0.6 : 1 }}
          >
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>Les affaires et les heures</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Reporte aussi les heures, jour pour jour. Les cellules déjà saisies, les jours
              fériés, les congés et les jours verrouillés sont laissés de côté. Les notes ne
              sont pas recopiées.
            </span>
          </button>
          {previousBusy ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Reprise en cours…</div>
          ) : null}
        </div>
      </Modal>

      {/* #90 — note du jour sur une cellule du Tableau */}
      <Modal
        open={Boolean(noteCell)}
        onClose={() => (noteBusy ? undefined : setNoteCell(null))}
        title="Note du jour"
        footer={
          <>
            <button
              type="button"
              onClick={() => setNoteCell(null)}
              disabled={noteBusy}
              style={secondaryBtn}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveNote}
              disabled={noteBusy}
              style={{ ...primaryBtn, opacity: noteBusy ? 0.6 : 1 }}
            >
              {noteBusy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        {noteCell ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {noteCell.row.affaire?.name ?? ''}
              {noteCell.row.task?.name ? ` — ${noteCell.row.task.name}` : ''} · {noteCell.date}
            </div>
            <textarea
              autoFocus
              rows={5}
              value={noteCell.value}
              onChange={(e) => setNoteCell({ ...noteCell, value: e.target.value })}
              placeholder="Ce qui a été fait ce jour-là…"
              style={{
                width: '100%',
                padding: 10,
                background: 'var(--glass)',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Vider le champ supprime la note. Elle est visible dans la vue Calendrier.
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Suppression d'une ligne entière du Tableau */}
      <Modal
        open={Boolean(deleteRow)}
        onClose={() => (deleteRowBusy ? undefined : setDeleteRow(null))}
        title="Supprimer la ligne"
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeleteRow(null)}
              disabled={deleteRowBusy}
              style={secondaryBtn}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={confirmDeleteRow}
              disabled={deleteRowBusy}
              style={{
                ...primaryBtn,
                background: 'var(--danger)',
                opacity: deleteRowBusy ? 0.6 : 1,
              }}
            >
              {deleteRowBusy ? 'Suppression…' : 'Supprimer'}
            </button>
          </>
        }
      >
        {deleteRow ? (() => {
          const entries = rowDeletableEntries(deleteRow);
          const total = entries.reduce((acc, c) => acc + (Number(c.hours) || 0), 0);
          const lockedCount = (data?.days ?? []).filter(
            (d) =>
              isDateLocked(d.date) &&
              cells[cellKey(deleteRow.affaireId, deleteRow.phaseId, deleteRow.taskId, d.date)]?.existingId,
          ).length;
          return (
            <div style={{ display: 'grid', gap: 10, fontSize: 14, color: 'var(--text)' }}>
              <div>
                <strong>{deleteRow.affaire?.name ?? '—'}</strong>
                {deleteRow.task?.name ? ` — ${deleteRow.task.name}` : ''}
              </div>
              <div>
                {entries.length > 0
                  ? `${entries.length} saisie(s) pour un total de ${formatHHMM(total)} seront supprimées sur cette semaine.`
                  : 'Aucune saisie sur cette semaine : la ligne sera simplement retirée.'}
              </div>
              {lockedCount > 0 ? (
                <div style={{ fontSize: 12, color: 'var(--warning)' }}>
                  {lockedCount} saisie(s) sur une période verrouillée seront conservées.
                </div>
              ) : null}
              {deleteRow.allocationId ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Cette affaire est planifiée : la ligne restera affichée avec ses suggestions,
                  le planning n'étant pas modifié.
                </div>
              ) : null}
            </div>
          );
        })() : null}
      </Modal>

      {/* Tâche d'un jour du Tableau (#90, #76) */}
      <Modal
        open={Boolean(taskCell)}
        onClose={() => (taskBusy ? undefined : setTaskCell(null))}
        title="Tâche du jour"
        size="lg"
        fill
        footer={
          <>
            <button
              type="button"
              onClick={() => setTaskCell(null)}
              disabled={taskBusy}
              style={secondaryBtn}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveCellTask}
              disabled={taskBusy}
              style={{ ...primaryBtn, opacity: taskBusy ? 0.6 : 1 }}
            >
              {taskBusy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        {taskCell ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {taskCell.row.affaire?.name ?? ''}
              {taskCell.row.phase?.name ? ` — phase ${taskCell.row.phase.name}` : ''}
              {' — '}
              {taskCell.date}
            </div>
            <TaskPicker
              affaireId={taskCell.row.affaireId}
              value={taskCell.value}
              onChange={(v) => setTaskCell({ ...taskCell, value: v })}
              disabled={taskBusy}
              inputId="cell-task"
            />
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {taskCell.cell.existingId
                ? 'Seules les heures de ce jour changent de tâche.'
                : `Les ${formatHHMM(taskCell.cell.hours)} prévues ce jour seront saisies sur la tâche choisie.`}
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Édition Person depuis le clic sur le nom (ADMIN) */}
      <PersonFormModal
        open={Boolean(editPersonId)}
        personId={editPersonId}
        onClose={() => setEditPersonId(null)}
        onSaved={() => {
          setEditPersonId(null);
          loadWeek();
        }}
      />

      <span style={{ display: 'none' }}><Trash2 size={1} /></span>
    </div>
  );
}
